import { router, usePage } from "@inertiajs/react";
import type { InertiaProps } from "@shared";
import { DeploySidebar } from "@components/deploy";
import DeployActions from "@components/deploy/DeployActions";
import {
  InProgressScreen,
  GoogleConnectScreen,
  InviteAcceptScreen,
  PaymentRequiredScreen,
  DeployCompleteScreen,
  ConnectionErrorScreen,
} from "@components/deploy/screens";
import { PaginationFooter } from "@components/shared/pagination-footer";
import { useDeployTasks, type DeployTaskRecord } from "@api/deploys.hooks";
import { useDeployContentScreen, type DeployScreen } from "@hooks/useDeployContentScreen";
import { useDeployType } from "@hooks/useDeployInstructions";
import { useDeployHeartbeat } from "@hooks/useDeployHeartbeat";

type DeployPageProps =
  InertiaProps.paths["/campaigns/{uuid}/deploy"]["get"]["responses"]["200"]["content"]["application/json"];

const SCREENS: Record<DeployScreen, React.ComponentType<{ actions?: React.ReactNode }>> = {
  "in-progress": InProgressScreen,
  "google-connect": GoogleConnectScreen,
  "invite-accept": InviteAcceptScreen,
  "payment-required": PaymentRequiredScreen,
  "deploy-complete": DeployCompleteScreen,
  "deploy-error": DeployCompleteScreen,
  "deploy-cancelled": DeployCompleteScreen,
  "connection-error": ConnectionErrorScreen,
};

export default function DeployPage() {
  const { deploy, project } = usePage<DeployPageProps>().props;
  const deployType = useDeployType();
  const isCampaignDeploy = deployType === "campaign";

  // Poll tasks every 3s, seeded from Inertia props
  const { data, isError } = useDeployTasks(deploy?.id, deploy?.tasks as DeployTaskRecord[] | undefined, deploy?.status);

  const tasks = data?.tasks;
  const deployStatus = data?.deploy_status ?? deploy?.status;

  useDeployHeartbeat(deploy?.id, deployStatus);

  const screen = useDeployContentScreen(tasks, deployStatus, isError);
  const isTerminal = screen === "deploy-complete" || screen === "deploy-error" || screen === "deploy-cancelled";

  const DeployScreen = SCREENS[screen];
  const actions = <DeployActions screen={screen} deployId={deploy?.id} />;

  return (
    <div className="h-full flex flex-col">
      <main className="flex-1 min-h-0 grid grid-cols-[minmax(360px,640px)_1fr] gap-x-3 px-3 py-3">
        <div className="min-h-0 overflow-hidden">
          <DeploySidebar tasks={tasks} deployStatus={deployStatus} />
        </div>
        <div className="min-h-0 overflow-auto border border-neutral-300 bg-white rounded-2xl">
          <div className="min-w-[540px] h-full">
            <DeployScreen actions={actions} />
          </div>
        </div>
      </main>

      <PaginationFooter.Root layout="full-bleed" canGoBack={isTerminal} canGoForward={isTerminal}>
        <PaginationFooter.BackButton />
        <PaginationFooter.Actions>
          <PaginationFooter.ContinueButton
            disabled={!isTerminal}
            onClick={
              isCampaignDeploy
                ? () => router.visit(`/projects/${project!.uuid}/performance`)
                : () => router.visit("/ads")
            }
          >
            {isCampaignDeploy ? "See Performance" : "Launch Ads"}
          </PaginationFooter.ContinueButton>
        </PaginationFooter.Actions>
      </PaginationFooter.Root>
    </div>
  );
}
